import { useCallback, useRef, useEffect, useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import { getMediaClient } from '@atlaskit/media-client-react';
import type { MediaClient } from '@atlaskit/media-client';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider';

type MediaNode = ADFEntity & {
	type: 'media' | 'mediaSingle' | 'mediaInline';
};
interface ImageProcessingResult {
	processedAdf: DocNode;
	hasChanges: boolean;
	error?: Error;
}

interface ImageProcessorOptions {
	mediaProvider?: Promise<MediaProvider>;
}

/**.
 * Hook that returns `function to process ADF documents, reuploading images from other contentId's collections to specified mediaProvider's collection
 *
 * @param options - Configuration options
 * @param options.mediaProvider - Promise that resolves to a MediaProvider instance, used for uploading images to the media provider's media collection
 * @returns Object containing processImagesInAdf function for processing ADF documents
 */
export const useMediaImageProcessor = ({ mediaProvider }: ImageProcessorOptions) => {
	const mediaProviderRef = useRef<Promise<MediaProvider> | Promise<void>>(Promise.resolve());
	const mediaClientCacheRef = useRef<Map<string, MediaClient>>(new Map());

	// Store media provider as a ref so it can be accessed mid-processing without recreating callbacks or triggering re-renders
	useEffect(() => {
		if (mediaProvider) {
			mediaProviderRef.current = mediaProvider;
		}
	}, [mediaProvider]);

	// Get or create media client for a specific content ID, used for retrieving the original image URL
	// Leverages a cache to avoid creating multiple media clients for the same content ID
	const getViewMediaClientForContentId = useCallback(
		async (originalContentId: string): Promise<MediaClient> => {
			// Check media client cache first
			const cache = mediaClientCacheRef.current;
			if (cache.has(originalContentId)) {
				return cache.get(originalContentId)!;
			}

			// Create renderer media provider for the original content
			const mediaProvider = await createRendererMediaProvider({
				contentId: originalContentId,
				isExternalShare: false,
			});

			// Get media client from the provider
			const mediaClient = getMediaClient(mediaProvider.viewMediaClientConfig);

			// Cache it
			cache.set(originalContentId, mediaClient);

			return mediaClient;
		},
		[],
	);

	// Processes ADF nodes recursively, reuploading images when necessary, and tracking whether ADF changes (reuploads) were made
	const processAdfNodes = async (
		adfContent: DocNode,
		mediaClient: MediaClient,
		targetCollection: string,
		getViewMediaClientForContentId: (contentId: string) => Promise<MediaClient>,
	): Promise<ImageProcessingResult> => {
		let hasChanges = false; // Stable state in closure

		// Inner recursive function - no change tracking in return type
		const processNode = async (node: any): Promise<any> => {
			if (!node || typeof node !== 'object') {
				return node;
			}

			// Process arrays like: `content: [{ type: "tableRow" }, { type: "mediaSingle" }]`
			if (Array.isArray(node)) {
				const processedItems = await Promise.all(node.map((item) => processNode(item)));
				return processedItems;
			}

			// Process single image node
			if (isImageNode(node) && needsReupload(node, targetCollection)) {
				hasChanges = true;
				try {
					const newImageData = await reuploadImageNode(
						node,
						mediaClient,
						targetCollection,
						getViewMediaClientForContentId,
					);
					return {
						...node,
						attrs: {
							...node.attrs,
							...newImageData,
						},
					};
				} catch (error) {
					return node; // Return original ADF on error
				}
			}

			// Process objects like: `{ type: "table", content: [...] } or { type: "mediaSingle", content: [...] }`
			const processedNode = { ...node };
			if (node.content && Array.isArray(node.content)) {
				processedNode.content = await processNode(node.content);
			}
			return processedNode;
		};

		const processedAdf = await processNode(adfContent);
		return { processedAdf: processedAdf as DocNode, hasChanges };
	};

	/**
	 * Process ADF content to fix image collection IDs by reuploading images to mediaProvider's collection
	 * @param <DocNode> adfContent - The ADF content to process
	 * @returns {Promise<ImageProcessingResult>} Promise resolving to an object containing:
	 *   - processedAdf: The processed ADF content with updated, reuploaded image collection IDs
	 *   - hasChanges: Boolean indicating if any changes were made to the ADF
	 *   - error?: Optional error if processing failed
	 */
	const processImagesInAdf = useCallback(
		async (adfContent: DocNode): Promise<ImageProcessingResult> => {
			try {
				const mediaProvider = await mediaProviderRef.current;

				if (!mediaProvider?.uploadMediaClientConfig || !mediaProvider.uploadParams?.collection) {
					return {
						processedAdf: adfContent,
						hasChanges: false,
						error: new Error('No media provider'),
					}; // Return original ADF if no media provider
				}

				let mediaClient;
				let targetCollection;
				try {
					mediaClient = getMediaClient(mediaProvider.uploadMediaClientConfig);
					targetCollection = mediaProvider.uploadParams.collection;
				} catch (error) {
					return {
						processedAdf: adfContent,
						hasChanges: false,
						error: enhanceError(error, 'Error getting media client'),
					}; // Return original ADF on error
				}

				try {
					return await processAdfNodes(
						adfContent,
						mediaClient,
						targetCollection,
						getViewMediaClientForContentId,
					);
				} catch (error) {
					return {
						processedAdf: adfContent,
						hasChanges: false,
						error: enhanceError(error, 'Error processing ADF nodes'),
					}; // Return original ADF on error
				}
			} catch (error) {
				return {
					processedAdf: adfContent,
					hasChanges: false,
					error: enhanceError(error, 'Error processing images in ADF'),
				}; // Return original ADF on error
			}
		},
		[getViewMediaClientForContentId],
	);

	return useMemo(() => {
		return {
			processImagesInAdf,
		};
	}, [processImagesInAdf]);
};

// Adds helpful message to error object
function enhanceError(error: Error, message: string) {
	return new Error(`${message}: ${error.message}`);
}

// Helper function to extract content ID from collection pattern
function extractContentIdFromCollection(collection: string): string | null {
	const match = collection.match(/^contentId-(\d+)$/);
	return match ? match[1] : null;
}

// Checks if a node is an image node
function isImageNode(node: ADFEntity): node is MediaNode {
	return (
		node?.type &&
		(node?.attrs?.id || node?.attrs?.url) &&
		['media', 'mediaInline', 'mediaSingle'].includes(node?.type)
	);
}

// Determines if an image node needs re-uploading
function needsReupload(node: MediaNode, targetCollection: string): boolean {
	return (
		!node.attrs?.collection || // no collection
		node.attrs.collection !== targetCollection // collection needs to change
	);
}

// Re-uploads an image node with correct collection ID
async function reuploadImageNode(
	node: MediaNode,
	editorMediaClient: MediaClient, // the media client we're using to upload the image
	targetCollection: string,
	getViewMediaClientForContentId: (contentId: string) => Promise<MediaClient>,
): Promise<{ id: string; collection: string }> {
	let sourceUrl = node.attrs?.url;

	// If we have an ID but wrong collection, try to get the URL from original content
	if (node.attrs?.id && node.attrs?.collection && !sourceUrl) {
		try {
			// Extract original content ID from collection pattern
			const originalContentId = extractContentIdFromCollection(node.attrs.collection);

			if (originalContentId) {
				// Get media client for the original content
				const originalViewMediaClient = await getViewMediaClientForContentId(originalContentId);

				// Use MediaClient's getImageUrl method to properly construct the absolute URL
				// This handles auth and baseUrl construction automatically
				sourceUrl = await originalViewMediaClient.getImageUrl(node.attrs.id, {
					collection: node.attrs.collection,
				});
			} else {
				throw new Error(`Invalid collection format: ${node.attrs.collection}`);
			}
		} catch (error) {
			// Fall back to using the original ID/collection if we can't get URL
			throw new Error(`Could not access source image: ${node.attrs.id}`);
		}
	}

	if (!sourceUrl) {
		throw new Error('No source URL available for image re-upload');
	}

	// Upload to target collection
	const uploader = await editorMediaClient.file.uploadExternal(sourceUrl, targetCollection);
	const result = await uploader;

	return {
		id: result.uploadableFileUpfrontIds.id,
		collection: targetCollection,
	};
}
